import {
  ExpenseByIdQuery,
  ExpenseFormDataQuery,
  UpdateExpenseItemInput,
} from '../../../__generated__/graphql';

export const MEAL_RECORD_TYPE_NAME = 'Meal';
export const MEAL_TYPE_BREAKFAST = 'breakfast';
export const MEAL_TYPE_LUNCH = 'lunch';
export const MEAL_TYPE_DINNER = 'dinner';

export const FIELD_BREAKFAST_PROVIDED = 'flair__Breakfast_Provided__c';
export const FIELD_LUNCH_PROVIDED = 'flair__Lunch_Provided__c';
export const FIELD_DINNER_PROVIDED = 'flair__Dinner_Provided__c';

export const MEAL_CONDITION_FIELDS = [
  FIELD_BREAKFAST_PROVIDED,
  FIELD_LUNCH_PROVIDED,
  FIELD_DINNER_PROVIDED,
] as const;

export const AUTO_SAVE_DELAY_MS = 800;

export type MealType = 'breakfast' | 'lunch' | 'dinner';

export type MealAllowance = {
  type: MealType;
  label: string;
  amount: number;
  checked: boolean;
};

export type MealDeductions = {
  breakfast: number;
  lunch: number;
  dinner: number;
};

export const createDefaultMealAllowances = (
  t: (key: string) => string,
): MealAllowance[] => [
  {
    type: MEAL_TYPE_BREAKFAST,
    label: t('expenses.perDiemForm.breakfastLabel'),
    amount: 0,
    checked: true,
  },
  {
    type: MEAL_TYPE_LUNCH,
    label: t('expenses.perDiemForm.lunchLabel'),
    amount: 0,
    checked: true,
  },
  {
    type: MEAL_TYPE_DINNER,
    label: t('expenses.perDiemForm.dinnerLabel'),
    amount: 0,
    checked: true,
  },
];

export const getMealExpenseItem = (
  expenseItems?: readonly NonNullable<
    ExpenseByIdQuery['expense']
  >['expenseItems'][number][],
) => {
  return expenseItems?.find(
    (item) => item.recordType?.Name === MEAL_RECORD_TYPE_NAME,
  );
};

export const getExtraFieldValue = (
  extraFields:
    | readonly NonNullable<
        ExpenseByIdQuery['expense']
      >['expenseItems'][number]['extraFields'][number][]
    | null
    | undefined,
  fieldName: string,
): boolean => {
  const field = extraFields?.find((ef) => ef.fieldApiName === fieldName);
  return field ? field.jsonValue === 'true' : false;
};

export const getMealProvidedStatus = (
  mealExpenseItem: NonNullable<
    ExpenseByIdQuery['expense']
  >['expenseItems'][number],
  mealType: MealType,
): boolean => {
  const directValue =
    mealType === MEAL_TYPE_BREAKFAST
      ? mealExpenseItem.flair__Breakfast_Provided__c
      : mealType === MEAL_TYPE_LUNCH
      ? mealExpenseItem.flair__Lunch_Provided__c
      : mealExpenseItem.flair__Dinner_Provided__c;

  const fieldName =
    mealType === MEAL_TYPE_BREAKFAST
      ? FIELD_BREAKFAST_PROVIDED
      : mealType === MEAL_TYPE_LUNCH
      ? FIELD_LUNCH_PROVIDED
      : FIELD_DINNER_PROVIDED;

  return (
    directValue || getExtraFieldValue(mealExpenseItem.extraFields, fieldName)
  );
};

export const findMealRule = (
  rules: ExpenseFormDataQuery['expenseRules'] | null | undefined,
  mealType: MealType,
): NonNullable<ExpenseFormDataQuery['expenseRules']>[number] | undefined => {
  if (!rules) return undefined;

  const fieldName =
    mealType === MEAL_TYPE_BREAKFAST
      ? FIELD_BREAKFAST_PROVIDED
      : mealType === MEAL_TYPE_LUNCH
      ? FIELD_LUNCH_PROVIDED
      : FIELD_DINNER_PROVIDED;

  return rules.find((rule) => rule.flair__Condition_Field__c === fieldName);
};

export const getMealPercentages = (
  rules: ExpenseFormDataQuery['expenseRules'] | null | undefined,
) => {
  const breakfastPercent = Math.abs(
    findMealRule(rules, MEAL_TYPE_BREAKFAST)?.flair__Percentage_Addition__c ||
      0,
  );
  const lunchPercent = Math.abs(
    findMealRule(rules, MEAL_TYPE_LUNCH)?.flair__Percentage_Addition__c || 0,
  );
  const dinnerPercent = Math.abs(
    findMealRule(rules, MEAL_TYPE_DINNER)?.flair__Percentage_Addition__c || 0,
  );

  return {
    breakfast: breakfastPercent,
    lunch: lunchPercent,
    dinner: dinnerPercent,
  };
};

export const calculateIncludedPercentage = (
  allowances: MealAllowance[],
  percentages: { breakfast: number; lunch: number; dinner: number },
): number => {
  return allowances
    .filter((meal) => meal.checked)
    .reduce((sum, meal) => sum + (percentages[meal.type] || 0), 0);
};

export const calculateMealDeductions = (
  fullAmount: number,
  includedPercentage: number,
  percentages: { breakfast: number; lunch: number; dinner: number },
): MealDeductions => {
  if (includedPercentage === 0) {
    return { breakfast: 0, lunch: 0, dinner: 0 };
  }

  const baseAmount = fullAmount / (includedPercentage / 100);
  const calculate = (percent: number) => (baseAmount * percent) / 100;

  return {
    breakfast: calculate(percentages.breakfast),
    lunch: calculate(percentages.lunch),
    dinner: calculate(percentages.dinner),
  };
};

export const buildMealAllowanceUpdateInput = (
  mealExpenseItem: NonNullable<
    ExpenseByIdQuery['expense']
  >['expenseItems'][number],
  mealType: MealType,
  checked: boolean,
): UpdateExpenseItemInput => {
  const isBreakfast = mealType === MEAL_TYPE_BREAKFAST;
  const isLunch = mealType === MEAL_TYPE_LUNCH;
  const isDinner = mealType === MEAL_TYPE_DINNER;

  return {
    id: mealExpenseItem.Id,
    extraFields: [],
    flair__Breakfast_Provided__c: isBreakfast
      ? !checked
      : mealExpenseItem.flair__Breakfast_Provided__c ?? null,
    flair__Lunch_Provided__c: isLunch
      ? !checked
      : mealExpenseItem.flair__Lunch_Provided__c ?? null,
    flair__Dinner_Provided__c: isDinner
      ? !checked
      : mealExpenseItem.flair__Dinner_Provided__c ?? null,
  } as unknown as UpdateExpenseItemInput;
};

export const initializeMealAllowances = (
  mealExpenseItem:
    | NonNullable<ExpenseByIdQuery['expense']>['expenseItems'][number]
    | null
    | undefined,
  t: (key: string) => string,
): MealAllowance[] => {
  if (!mealExpenseItem) {
    return createDefaultMealAllowances(t);
  }

  return createDefaultMealAllowances(t).map((allowance) => ({
    ...allowance,
    checked: !getMealProvidedStatus(mealExpenseItem, allowance.type),
  }));
};

export const hasMealAllowanceRules = (
  expenseRules: ExpenseFormDataQuery['expenseRules'] | null | undefined,
): boolean => {
  if (!expenseRules?.length) return false;
  return expenseRules.some((rule) =>
    MEAL_CONDITION_FIELDS.includes(
      rule.flair__Condition_Field__c as (typeof MEAL_CONDITION_FIELDS)[number],
    ),
  );
};
